import { useCart } from 'apps/vtex/hooks/useCart.ts'
import {
    trackAddPaymentInfo,
    trackAddShippingInfo,
    trackBeginCheckout,
} from './analytics.ts'
import { loadTrinioSdk } from './assets.ts'
import { TRINIO_CONFIG } from './config.ts'
import type { TrinioApp } from './types.ts'

let app: TrinioApp | null = null
let initialization: Promise<TrinioApp | null> | null = null

const redirectToFallback = (href: string = TRINIO_CONFIG.fallbackHref) => {
    globalThis.location.assign(href)
}

export const initTrinioCheckout = () => {
    if (typeof document === 'undefined') return Promise.resolve(null)
    if (app) return Promise.resolve(app)
    if (initialization) return initialization

    initialization = (async () => {
        try {
            const sdk = await loadTrinioSdk(
                TRINIO_CONFIG.cssHref,
                TRINIO_CONFIG.scriptHref,
            )
            const vtex = globalThis.window.vtex
            const checkout = sdk.createCheckout(TRINIO_CONFIG.merchantId, {
                vtexAccountName: vtex?.accountName ?? TRINIO_CONFIG.vtexAccountName,
                vtexEndpointAPI: vtex?.endpointAPI ?? TRINIO_CONFIG.vtexEndpointAPI,
                vtexPublicEndpoint: vtex?.renderRuntime?.publicEndpoint ??
                    TRINIO_CONFIG.vtexPublicEndpoint,
                vtexWorkspace: vtex?.renderRuntime?.workspace ?? TRINIO_CONFIG.vtexWorkspace,
                onErrorFallback: (error) => {
                    console.error('[Trinio] Checkout error:', error)
                    redirectToFallback()
                },
                trackBeginCheckout,
                trackAddShippingInfo,
                trackAddPaymentInfo,
            })

            app = await checkout.startApp()
            return app
        } catch (error) {
            console.error('[Trinio] Initialization error:', error)
            initialization = null
            return null
        }
    })()

    return initialization
}

export const openTrinioCheckout = async (
    orderFormId: string,
    fallbackHref: string,
) => {
    const checkoutApp = app ?? (await initTrinioCheckout())
    const currentOrderFormId = orderFormId || useCart().cart.value?.orderFormId

    if (!checkoutApp?.enabled || !currentOrderFormId) {
        redirectToFallback(fallbackHref)
        return
    }

    const result = await checkoutApp.open({ orderFormId: currentOrderFormId })

    if (result.error) {
        console.error('[Trinio] Checkout open error:', result.error)
        redirectToFallback(fallbackHref)
    }
}
